import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Draggable } from '@fullcalendar/interaction';
import {
  LayoutGrid,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  Calendar,
  ListTodo,
  Users,
  X,
  Maximize2,
  Minimize2,
  ExternalLink,
  MessageCircle,
  Square,
  CheckSquare,
  List,
  Paperclip,
  Search
} from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import { MiniCalendar } from '../calendar/MiniCalendar';
import { useTeamPlanningMode } from '../../contexts/TeamPlanningModeContext';
import { useProjectStore } from '../../lib/stores/projectStore';
import { useApp } from '../AppProvider';
import {
  getOverdueTasks,
  getScheduledTaskIds,
  getScheduledHoursByTask,
  groupTasksByProject,
  calculateUserWorkload,
  formatHours,
  getCapacityColor,
  getDaysOverdue
} from '../../lib/utils/planningUtils';
import type { Task, Project } from '../../lib/types';
import { URGENCY_CONFIG } from '../../lib/types';
import { ProjectDetailsModal } from '../projects/ProjectDetailsModal';
import { TaskDrawer } from '../projects/TaskDrawer';

// ============================================
// TYPES
// ============================================

interface TeamPlanningSidebarLeftProps {
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  onClose?: () => void;
  isMobile?: boolean;
}

// ============================================
// DRAGGABLE TASK ITEM (Minimal style)
// ============================================

// Priority label mapping
const PRIORITY_LABELS: Record<string, string> = {
  urgent: 'Urgente',
  high: 'Alta',
  medium: 'Media',
  low: 'Bassa'
};

// Project status label mapping
const PROJECT_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  planning: { label: 'Planning', color: '#eab308' },
  active: { label: 'Attivo', color: '#22c55e' },
  on_hold: { label: 'In Pausa', color: '#f97316' },
  completed: { label: 'Completato', color: '#3b82f6' },
  cancelled: { label: 'Annullato', color: '#6b7280' }
};

interface User {
  id: string;
  full_name?: string | null;
  email: string;
  profile_image_url?: string | null;
}

// Palette colori per utenti — allineata a TeamPlanningView/TeamCalendarGrid (indice nella lista users)
const TEAM_USER_COLORS = [
  '#6B7280', '#78716C', '#5B7B99', '#8B7355', '#7C6F9C',
  '#2383E2', '#5F7470', '#9B8B84', '#807182', '#94A3B8'
];

// Helper function for smart due date formatting (like TaskItem)
function getDueDateInfo(dueDate: string) {
  const today = new Date();
  const tomorrow = new Date();
  const dayAfterTomorrow = new Date();
  const yesterday = new Date();

  tomorrow.setDate(today.getDate() + 1);
  dayAfterTomorrow.setDate(today.getDate() + 2);
  yesterday.setDate(today.getDate() - 1);

  const due = new Date(dueDate);

  // Reset time to compare only dates
  today.setHours(0, 0, 0, 0);
  tomorrow.setHours(0, 0, 0, 0);
  dayAfterTomorrow.setHours(0, 0, 0, 0);
  yesterday.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);

  if (due.getTime() === today.getTime()) {
    return { text: 'Oggi', color: 'text-orange-600 dark:text-orange-400' };
  } else if (due.getTime() === tomorrow.getTime()) {
    return { text: 'Domani', color: 'text-orange-600 dark:text-orange-400' };
  } else if (due.getTime() === dayAfterTomorrow.getTime()) {
    return { text: 'Dopodomani', color: 'text-orange-600 dark:text-orange-400' };
  } else if (due.getTime() === yesterday.getTime()) {
    return { text: 'Ieri', color: 'text-red-600 dark:text-red-400' };
  } else if (due < today) {
    return { text: due.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' }), color: 'text-red-600 dark:text-red-400' };
  } else if (due <= new Date(today.getTime() + 3 * 24 * 60 * 60 * 1000)) {
    return { text: due.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' }), color: 'text-orange-600 dark:text-orange-400' };
  } else {
    return { text: due.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' }), color: 'text-[#787774] dark:text-gray-400' };
  }
}

// Helper to get initials
function getInitials(name: string): string {
  return name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// TaskItemView - visualizzazione task (non trascinabile, click per aprire drawer)
// Indicatore stimato-vs-schedulato per un task.
// estimated = task.estimated_hours (colonna DB), scheduled = ore sommate dai work slot.
function EstimateScheduleBadge({
  estimatedHours,
  scheduledHours,
  size = 'sm'
}: {
  estimatedHours: number;
  scheduledHours: number;
  size?: 'sm' | 'xs';
}) {
  const hasEstimate = estimatedHours > 0;
  // Nessuna stima: mostra solo le ore schedulate (se >0), altrimenti nulla
  if (!hasEstimate) {
    if (scheduledHours <= 0) return null;
    return (
      <span
        className={`${size === 'xs' ? 'text-[9px]' : 'text-[10px]'} font-medium text-[#787774] dark:text-gray-400 flex-shrink-0`}
        title={`Schedulate ${formatHours(scheduledHours)} (nessuna stima)`}
      >
        {formatHours(scheduledHours)}
      </span>
    );
  }

  const fmt = (h: number) => `${Math.round(h * 10) / 10}h`;
  const textSize = size === 'xs' ? 'text-[9px]' : 'text-[10px]';

  // Coperto: schedulato >= stima
  if (scheduledHours >= estimatedHours) {
    return (
      <span
        className={`${textSize} font-medium flex-shrink-0`}
        style={{ color: '#0f7b34' }}
        title={`Coperto: ${fmt(scheduledHours)} schedulate su ${fmt(estimatedHours)} stimate`}
      >
        ✓ {fmt(scheduledHours)}/{fmt(estimatedHours)}
      </span>
    );
  }

  // Parziale: 0 < schedulato < stima
  if (scheduledHours > 0) {
    return (
      <span
        className={`${textSize} font-medium flex-shrink-0`}
        style={{ color: '#b25e09' }}
        title={`Parziale: ${fmt(scheduledHours)} schedulate su ${fmt(estimatedHours)} stimate`}
      >
        {fmt(scheduledHours)}/{fmt(estimatedHours)}
      </span>
    );
  }

  // Da schedulare: schedulato == 0, stima > 0
  return (
    <span
      className={`${textSize} font-medium text-[#787774] dark:text-gray-400 flex-shrink-0`}
      title={`Da schedulare: 0 su ${fmt(estimatedHours)} stimate`}
    >
      0/{fmt(estimatedHours)}
    </span>
  );
}

function TaskItemView({
  task,
  project,
  showOverdueBadge = false,
  isExpanded = false,
  isScheduled = false,
  scheduledHours = 0,
  users = [],
  onCompleteTask,
  onTaskClick
}: {
  task: Task;
  project?: Project;
  showOverdueBadge?: boolean;
  isExpanded?: boolean;
  isScheduled?: boolean;
  scheduledHours?: number;
  users?: User[];
  onCompleteTask?: (taskId: string) => void;
  onTaskClick?: (task: Task) => void;
}) {
  const daysOverdue = task.due_date ? getDaysOverdue(task.due_date) : 0;
  const isCompleted = task.status === 'completed';
  const estimatedHours = task.estimated_hours ?? 0;

  // Get assignee info
  const assigneeFromUsers = users.find(u => u.id === task.assigned_to);
  const assigneeName = task.assignee?.full_name || assigneeFromUsers?.full_name || task.assignee?.email?.split('@')[0] || assigneeFromUsers?.email?.split('@')[0];
  const assigneeAvatar = assigneeFromUsers?.profile_image_url;

  // Indicators
  const hasChecklist = task.checklist && task.checklist.length > 0;
  const checklistProgress = hasChecklist
    ? `${task.checklist!.filter(item => item.is_completed).length}/${task.checklist!.length}`
    : null;
  const hasAttachments = task.attachments && task.attachments.length > 0;
  const hasComments = task.comments && task.comments.filter(c => !c.is_system_comment).length > 0;
  const commentsCount = task.comments?.filter(c => !c.is_system_comment).length ?? 0;

  // Due date info
  const dueDateInfo = task.due_date ? getDueDateInfo(task.due_date) : null;

  // Handle status change (toggle complete)
  const handleStatusChange = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onCompleteTask) {
      onCompleteTask(task.id);
    }
  };

  // Handle task click
  const handleTaskClick = (e: React.MouseEvent) => {
    // Only trigger if not clicking on the checkbox button
    if ((e.target as HTMLElement).closest('button')) return;
    onTaskClick?.(task);
  };

  // Priority indicator (matching TaskItem style)
  const getPriorityIndicator = () => {
    switch (task.priority) {
      case 'urgent':
        return <div className="w-1 h-4 bg-red-500 rounded-sm" />;
      case 'high':
        return <div className="w-1 h-4 bg-orange-500 rounded-sm" />;
      case 'medium':
        return <div className="w-1 h-4 bg-yellow-500 rounded-sm" />;
      case 'low':
        return <div className="w-1 h-4 bg-green-500 rounded-sm" />;
      default:
        return <div className="w-1 h-4 bg-gray-300 rounded-sm" />;
    }
  };

  // Expanded view - matches TaskItem style
  if (isExpanded) {
    return (
      <div
        className={`group flex items-center py-1.5 px-2
                   hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.04]
                   cursor-pointer border-l-2 border-transparent
                   hover:border-l-[#2383E2] dark:hover:border-l-blue-400
                   transition-all duration-150 ${isCompleted ? 'opacity-60' : ''}`}
        onClick={handleTaskClick}
      >
        {/* Priority Bar */}
        <div className="flex-shrink-0 mr-2">
          {getPriorityIndicator()}
        </div>

        {/* Checkbox */}
        <div className="flex-shrink-0 mr-3 flex items-center">
          <button
            onClick={handleStatusChange}
            className="flex items-center justify-center hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-gray-700 rounded-md p-0.5 transition-colors"
          >
            {isCompleted ? (
              <CheckSquare className="h-4 w-4 text-[#2383E2] dark:text-green-600" />
            ) : (
              <Square className="h-4 w-4 text-[#787774] dark:text-gray-400 hover:text-[#37352F] dark:hover:text-gray-300" />
            )}
          </button>
        </div>

        {/* Task Content */}
        <div className="flex-1 min-w-0 mr-3">
          <div className="flex items-center space-x-1">
            <p className={`text-sm font-medium truncate ${
              isCompleted
                ? 'text-[#787774] dark:text-gray-500 line-through'
                : 'text-[#37352F] dark:text-gray-100'
            }`}>
              {task.title}
            </p>
          </div>
        </div>

        {/* Indicators */}
        <div className="flex items-center space-x-1 mr-2 flex-shrink-0">
          {/* Checklist indicator */}
          {hasChecklist && (
            <div className="flex items-center">
              <List className="h-3 w-3 text-[#787774] dark:text-gray-400" />
              <span className="text-xs text-[#787774] dark:text-gray-500 ml-0.5">
                {checklistProgress}
              </span>
            </div>
          )}

          {/* Attachment indicator */}
          {hasAttachments && (
            <Paperclip className="h-3 w-3 text-[#787774] dark:text-gray-400" />
          )}

          {/* Comments indicator */}
          {hasComments && (
            <div className="flex items-center">
              <MessageCircle className="h-3 w-3 text-[#787774] dark:text-gray-400" />
              <span className="text-xs text-[#787774] dark:text-gray-500 ml-0.5">
                {commentsCount}
              </span>
            </div>
          )}

          {/* Stimato vs schedulato */}
          <EstimateScheduleBadge
            estimatedHours={estimatedHours}
            scheduledHours={scheduledHours}
            size="sm"
          />
        </div>

        {/* Due date */}
        {dueDateInfo && (
          <span className={`text-xs font-medium mr-2 flex-shrink-0 ${dueDateInfo.color}`}>
            {dueDateInfo.text}
            {showOverdueBadge && daysOverdue > 0 && ` (-${daysOverdue}g)`}
          </span>
        )}

        {/* Scheduled indicator */}
        {isScheduled && (
          <Calendar className="h-3 w-3 text-blue-500 mr-2 flex-shrink-0" title="Schedulata a calendario" />
        )}

        {/* Assignee */}
        {assigneeName && (
          <div
            className="h-6 w-6 rounded-full bg-[rgba(55,53,47,0.08)] dark:bg-blue-900 flex items-center justify-center border border-[rgba(55,53,47,0.16)] dark:border-blue-700 flex-shrink-0"
            title={assigneeName}
          >
            {assigneeAvatar ? (
              <img src={assigneeAvatar} alt="" className="h-6 w-6 rounded-full object-cover" />
            ) : (
              <span className="text-xs font-medium text-[#37352F] dark:text-blue-300">
                {getInitials(assigneeName)}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }

  // Compact view (default) - simplified but still TaskItem-like
  return (
    <div
      className={`group flex items-center py-1 px-1
                 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.04]
                 cursor-pointer border-l-2 border-transparent
                 hover:border-l-[#2383E2] dark:hover:border-l-blue-400
                 transition-all duration-150 ${isCompleted ? 'opacity-60' : ''}`}
      onClick={handleTaskClick}
    >
      {/* Priority Bar */}
      <div className="flex-shrink-0 mr-1">
        {getPriorityIndicator()}
      </div>

      {/* Checkbox */}
      <div className="flex-shrink-0 mr-2 flex items-center">
        <button
          onClick={handleStatusChange}
          className="flex items-center justify-center hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-gray-700 rounded-md p-0.5 transition-colors"
        >
          {isCompleted ? (
            <CheckSquare className="h-3 w-3 text-[#2383E2] dark:text-green-600" />
          ) : (
            <Square className="h-3 w-3 text-[#787774] dark:text-gray-400 hover:text-[#37352F] dark:hover:text-gray-300" />
          )}
        </button>
      </div>

      {/* Task title */}
      <div className="flex-1 min-w-0">
        <p className={`text-xs font-medium truncate ${
          isCompleted
            ? 'text-[#787774] dark:text-gray-500 line-through'
            : 'text-[#37352F] dark:text-gray-100'
        }`}>
          {task.title}
        </p>
      </div>

      {/* Stimato vs schedulato */}
      <div className="flex-shrink-0 ml-1 mr-1">
        <EstimateScheduleBadge
          estimatedHours={estimatedHours}
          scheduledHours={scheduledHours}
          size="xs"
        />
      </div>

      {/* Overdue badge */}
      {showOverdueBadge && daysOverdue > 0 && (
        <span className="text-[10px] text-red-600 font-medium flex-shrink-0 mr-1">
          -{daysOverdue}g
        </span>
      )}

      {/* Scheduled indicator */}
      {isScheduled && (
        <Calendar className="h-2.5 w-2.5 text-blue-500 flex-shrink-0" title="Schedulata" />
      )}
    </div>
  );
}

// ============================================
// COLLAPSIBLE SECTION
// ============================================

function CollapsibleSection({
  title,
  count,
  icon: Icon,
  iconColor,
  children,
  defaultExpanded = false,
  onExpand,
  isSidebarExpanded = false
}: {
  title: string;
  count: number;
  icon: React.ElementType;
  iconColor: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  onExpand?: () => void;
  isSidebarExpanded?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div className="border-b border-black/5 dark:border-white/5 last:border-b-0">
      <div className="flex items-center">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex-1 flex items-center gap-2 py-2 px-1 hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
        >
          {isExpanded ? (
            <ChevronDown className="h-3 w-3 text-black/50 dark:text-white/50 flex-shrink-0" />
          ) : (
            <ChevronRight className="h-3 w-3 text-black/50 dark:text-white/50 flex-shrink-0" />
          )}
          <Icon className="h-3.5 w-3.5 flex-shrink-0" style={{ color: iconColor }} />
          <span className="text-xs font-medium text-black/70 dark:text-white/70 flex-1 text-left">
            {title}
          </span>
          {count > 0 && (
            <span
              className="text-[10px] font-medium px-1.5 py-0.5 rounded-full"
              style={{ backgroundColor: `${iconColor}20`, color: iconColor }}
            >
              {count}
            </span>
          )}
        </button>
        {onExpand && count > 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onExpand();
            }}
            className="p-1.5 mr-1 rounded hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
            title={isSidebarExpanded ? "Riduci sidebar" : "Espandi sidebar"}
          >
            {isSidebarExpanded ? (
              <Minimize2 className="h-3 w-3 text-black/40 dark:text-white/40" />
            ) : (
              <Maximize2 className="h-3 w-3 text-black/40 dark:text-white/40" />
            )}
          </button>
        )}
      </div>

      {isExpanded && (
        <div className="pb-2">
          {children}
        </div>
      )}
    </div>
  );
}

// ============================================
// PROJECT GROUP (Collapsible)
// ============================================

function ProjectGroup({
  projectId,
  tasks,
  project,
  showOverdueBadge = false,
  defaultExpanded = false,
  isSidebarExpanded = false,
  onOpenProject,
  users = [],
  onCompleteTask,
  onTaskClick,
  scheduledTaskIds,
  scheduledHoursByTask
}: {
  projectId: string;
  tasks: Task[];
  project?: Project;
  showOverdueBadge?: boolean;
  defaultExpanded?: boolean;
  isSidebarExpanded?: boolean;
  onOpenProject?: (projectId: string) => void;
  users?: User[];
  onCompleteTask?: (taskId: string) => void;
  onTaskClick?: (task: Task) => void;
  scheduledTaskIds?: Set<string>;
  scheduledHoursByTask?: Map<string, number>;
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const clientName = project?.client?.business_name;
  const projectStatus = project?.status || 'active';
  const projectUrgency = project?.urgency || 'medium';
  const statusConfig = PROJECT_STATUS_LABELS[projectStatus] || PROJECT_STATUS_LABELS.active;
  const urgencyConfig = URGENCY_CONFIG[projectUrgency as keyof typeof URGENCY_CONFIG] || URGENCY_CONFIG.medium;
  const pmName = project?.project_manager?.full_name || project?.project_manager?.email?.split('@')[0];
  const dueDate = project?.due_date;

  const handleOpenProject = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (project?.id && onOpenProject) {
      onOpenProject(project.id);
    }
  };

  // Expanded view - minimal Notion-like style (no card container)
  // Il progetto è trascinabile, le task sono solo visualizzazione
  if (isSidebarExpanded) {
    return (
      <div className="mb-2">
        {/* Project Header - DRAGGABLE */}
        <div
          className="fc-draggable-project flex items-center gap-2 py-2 px-1 cursor-grab active:cursor-grabbing"
          data-project-id={project?.id || projectId}
          data-project-name={project?.name || 'Progetto'}
          data-project-color={project?.metadata?.color || '#10B981'}
        >
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-2 flex-1 min-w-0 hover:bg-black/5 dark:hover:bg-white/5 py-1.5 px-2 rounded transition-colors"
          >
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5 text-black/40 dark:text-white/40 flex-shrink-0" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-black/40 dark:text-white/40 flex-shrink-0" />
            )}
            <div
              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: project?.metadata?.color || '#10B981' }}
            />
            <div className="flex-1 min-w-0 text-left">
              <span className="text-sm font-medium text-black/80 dark:text-white/80 truncate">
                {project?.name || 'Progetto'}
              </span>
              {clientName && (
                <span className="text-xs text-black/40 dark:text-white/40 ml-2">
                  · {clientName}
                </span>
              )}
              <span className="text-xs text-black/40 dark:text-white/40 ml-2">
                ({tasks.length})
              </span>
            </div>
          </button>

          {/* Minimal badges inline */}
          <span
            className="text-[9px] font-medium px-1.5 py-0.5 rounded flex-shrink-0"
            style={{ backgroundColor: `${statusConfig.color}20`, color: statusConfig.color }}
          >
            {statusConfig.label}
          </span>

          {/* Open project button */}
          <button
            onClick={handleOpenProject}
            className="p-1 rounded hover:bg-black/10 dark:hover:bg-white/10 transition-colors flex-shrink-0"
            title="Apri progetto"
          >
            <ExternalLink className="h-3 w-3 text-black/40 dark:text-white/40" />
          </button>
        </div>

        {/* Tasks list - non trascinabili, click per aprire drawer */}
        {isExpanded && (
          <div className="ml-5 border-l border-black/5 dark:border-white/5 pl-2">
            {tasks.map(task => (
              <TaskItemView
                key={task.id}
                task={task}
                project={project}
                showOverdueBadge={showOverdueBadge}
                isExpanded={true}
                isScheduled={scheduledTaskIds?.has(task.id)}
                scheduledHours={scheduledHoursByTask?.get(task.id) ?? 0}
                users={users}
                onCompleteTask={onCompleteTask}
                onTaskClick={onTaskClick}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Compact view (default) - DRAGGABLE
  return (
    <div
      className="fc-draggable-project ml-2 cursor-grab active:cursor-grabbing"
      data-project-id={project?.id || projectId}
      data-project-name={project?.name || 'Progetto'}
      data-project-color={project?.metadata?.color || '#10B981'}
    >
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-2 py-1 px-1 hover:bg-black/5 dark:hover:bg-white/5 rounded transition-colors"
      >
        {isExpanded ? (
          <ChevronDown className="h-2.5 w-2.5 text-black/40 dark:text-white/40 flex-shrink-0" />
        ) : (
          <ChevronRight className="h-2.5 w-2.5 text-black/40 dark:text-white/40 flex-shrink-0" />
        )}
        <div
          className="w-2 h-2 rounded-full flex-shrink-0"
          style={{ backgroundColor: project?.metadata?.color || '#10B981' }}
        />
        <div className="flex-1 min-w-0 text-left">
          <span className="text-[11px] text-black/60 dark:text-white/60 truncate block">
            {project?.name || 'Progetto'}
          </span>
          {clientName && (
            <span className="text-[9px] text-black/40 dark:text-white/40 truncate block">
              {clientName}
            </span>
          )}
        </div>
        <span className="text-[10px] text-black/40 dark:text-white/40 flex-shrink-0">
          {tasks.length}
        </span>
      </button>

      {isExpanded && (
        <div className="ml-4 mt-1 space-y-0.5">
          {tasks.map(task => (
            <TaskItemView
              key={task.id}
              task={task}
              project={project}
              showOverdueBadge={showOverdueBadge}
              isExpanded={false}
              isScheduled={scheduledTaskIds?.has(task.id)}
              scheduledHours={scheduledHoursByTask?.get(task.id) ?? 0}
              users={users}
              onCompleteTask={onCompleteTask}
              onTaskClick={onTaskClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// WORKLOAD ITEM
// ============================================

function WorkloadItem({
  user,
  hours,
  maxHours = 40
}: {
  user: { id: string; full_name?: string | null; email: string; profile_image_url?: string | null };
  hours: number;
  maxHours?: number;
}) {
  const percentage = Math.min(100, (hours / maxHours) * 100);
  const color = getCapacityColor(hours, maxHours);
  const overloaded = hours > maxHours;

  return (
    <div className="flex items-center gap-2 py-1 px-2">
      {/* Avatar */}
      {user.profile_image_url ? (
        <img
          src={user.profile_image_url}
          alt=""
          className="w-5 h-5 rounded-full object-cover flex-shrink-0"
        />
      ) : (
        <div className="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
          <span className="text-[9px] font-medium text-gray-600 dark:text-gray-300">
            {(user.full_name || user.email).charAt(0).toUpperCase()}
          </span>
        </div>
      )}

      {/* Name and progress */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-0.5 gap-1">
          <span className="flex items-center gap-1 min-w-0">
            <span className="text-[11px] text-black/70 dark:text-white/70 truncate">
              {user.full_name || user.email.split('@')[0]}
            </span>
            {overloaded && (
              <span
                className="text-[8px] font-medium px-1 py-px rounded-sm flex-shrink-0"
                style={{ backgroundColor: '#b91c1c1a', color: '#b91c1c' }}
                title="Sovraccarico: oltre 40h settimanali"
              >
                ⚠
              </span>
            )}
          </span>
          <span className="text-[10px] font-medium flex-shrink-0" style={{ color }}>
            {formatHours(hours)} / {maxHours}h
          </span>
        </div>
        <div className="h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${percentage}%`, backgroundColor: color }}
          />
        </div>
      </div>
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export function TeamPlanningSidebarLeft({
  isCollapsed = false,
  onToggleCollapse,
  onClose,
  isMobile = false
}: TeamPlanningSidebarLeftProps) {
  const { isLight } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const { updateTask, deleteTask, createTaskComment } = useProjectStore();
  const { toast } = useApp();

  // State for project details modal
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  // State for task drawer
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [showTaskDrawer, setShowTaskDrawer] = useState(false);

  // Search query to filter tasks/projects in the sections
  const [searchQuery, setSearchQuery] = useState('');

  // Get data from context
  const {
    currentDate,
    onDateSelect,
    tasks,
    projects,
    workSlots,
    users,
    weekStart,
    weekEnd,
    onBack,
    isSidebarExpanded,
    setSidebarExpanded,
    selectedUserIds,
    onUserToggle,
    setSelectedUserIds
  } = useTeamPlanningMode();

  // Toggle sidebar expansion
  const handleToggleExpanded = () => setSidebarExpanded(!isSidebarExpanded);

  // Handle opening project in modal
  const handleOpenProject = (projectId: string) => {
    setSelectedProjectId(projectId);
  };

  // Handle completing a task
  const handleCompleteTask = async (taskId: string) => {
    if (!window.confirm('Sei sicuro di voler completare questa task?')) {
      return;
    }
    try {
      await updateTask(taskId, { status: 'completed', completed_at: new Date().toISOString() });
      toast.success('Task completata');
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error('Errore nel completare la task');
    }
  };

  // Handle opening task in drawer
  const handleOpenTask = (task: Task) => {
    setSelectedTask(task);
    setShowTaskDrawer(true);
  };

  // Handler for TaskDrawer update
  const handleUpdateTask = async (taskId: string, updates: Partial<Task>) => {
    await updateTask(taskId, updates);
  };

  // Handler for TaskDrawer delete
  const handleDeleteTask = async (taskId: string) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa task?')) {
      return;
    }
    await deleteTask(taskId);
    setShowTaskDrawer(false);
    setSelectedTask(null);
    toast.success('Task eliminata');
  };

  // Handler for TaskDrawer add comment
  const handleAddComment = async (taskId: string, content: string) => {
    await createTaskComment(taskId, content);
  };

  // Project map
  const projectMap = useMemo(() => {
    return new Map(projects.map(p => [p.id, p]));
  }, [projects]);

  // Normalized search query + matcher (filters by task title and project/client name)
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const matchesSearch = useCallback((task: Task): boolean => {
    if (!normalizedQuery) return true;
    const project = projectMap.get(task.project_id);
    const haystack = [
      task.title,
      project?.name,
      project?.client?.business_name
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(normalizedQuery);
  }, [normalizedQuery, projectMap]);

  // Overdue tasks grouped by project (excluding archived projects)
  const overdueTasks = useMemo(() => {
    return getOverdueTasks(tasks).filter(t => projectMap.has(t.project_id) && matchesSearch(t));
  }, [tasks, projectMap, matchesSearch]);
  const overdueByProject = useMemo(() => groupTasksByProject(overdueTasks), [overdueTasks]);

  // Set di task schedulate per lookup veloce
  const scheduledTaskIds = useMemo(() => {
    return getScheduledTaskIds(workSlots);
  }, [workSlots]);

  // Mappa taskId -> ore totali schedulate (somma durate work slot), per indicatore stimato-vs-schedulato
  const scheduledHoursByTask = useMemo(() => {
    return getScheduledHoursByTask(workSlots);
  }, [workSlots]);

  // "Da Lavorare" - TUTTE le task non completate, raggruppate per progetto
  const allTasksByProject = useMemo(() => {
    // Raggruppa tutte le task per progetto (escludendo header e completate)
    const tasksByProject = new Map<string, Task[]>();
    for (const task of tasks) {
      if (task.is_header || task.status === 'completed') continue;
      if (!projectMap.has(task.project_id)) continue; // Escludi progetti archiviati
      if (!matchesSearch(task)) continue; // Filtro ricerca

      if (!tasksByProject.has(task.project_id)) {
        tasksByProject.set(task.project_id, []);
      }
      tasksByProject.get(task.project_id)!.push(task);
    }

    return tasksByProject;
  }, [tasks, projectMap, matchesSearch]);

  // Count totale task da lavorare
  const totalTasksToWork = useMemo(() => {
    return Array.from(allTasksByProject.values()).reduce((sum, tasks) => sum + tasks.length, 0);
  }, [allTasksByProject]);

  // Workload per user
  const userWorkloads = useMemo(() => {
    return users.map(user => ({
      user,
      workload: calculateUserWorkload(user.id, workSlots, weekStart, weekEnd)
    }));
  }, [users, workSlots, weekStart, weekEnd]);

  // Colore per utente — indice nella lista completa users (allineato al calendario)
  const userColorMap = useMemo(() => {
    const map = new Map<string, string>();
    users.forEach((u, i) => map.set(u.id, TEAM_USER_COLORS[i % TEAM_USER_COLORS.length]));
    return map;
  }, [users]);

  // TEAM: utenti assegnati ad almeno un task di progetto (no elenco infinito), ordinati per nome.
  // Empty-trap: se NESSUN utente è assegnato ad alcun task → fallback su TUTTI gli utenti dell'org,
  // così il PM può comunque selezionare il team (altrimenti la sezione resterebbe vuota e inutile).
  const sortByName = (a: User, b: User) =>
    (a.full_name || a.email).toLowerCase().localeCompare((b.full_name || b.email).toLowerCase());

  const assignedUsers = useMemo(() => {
    const assignedIds = new Set<string>();
    for (const task of tasks) {
      if (task.assigned_to) assignedIds.add(task.assigned_to);
    }
    return users.filter(u => assignedIds.has(u.id)).sort(sortByName);
  }, [users, tasks]);

  // Nessun assegnatario → mostro tutto il team
  const noAssignees = assignedUsers.length === 0;
  const allUsersSorted = useMemo(() => [...users].sort(sortByName), [users]);
  const assignableUsers = noAssignees ? allUsersSorted : assignedUsers;

  // Select/deselect all (limitato agli utenti assegnabili)
  const handleSelectAllAssignable = useCallback(() => {
    setSelectedUserIds(assignableUsers.map(u => u.id));
  }, [assignableUsers, setSelectedUserIds]);

  const handleDeselectAllUsers = useCallback(() => {
    setSelectedUserIds([]);
  }, [setSelectedUserIds]);

  // Initialize FullCalendar Draggable - PROGETTI trascinabili (non task)
  useEffect(() => {
    const containerEl = containerRef.current;
    if (!containerEl) return;

    const draggable = new Draggable(containerEl, {
      itemSelector: '.fc-draggable-project',
      eventData: (eventEl) => {
        const projectId = eventEl.getAttribute('data-project-id');
        const projectName = eventEl.getAttribute('data-project-name');
        const projectColor = eventEl.getAttribute('data-project-color');

        return {
          title: projectName || 'Progetto',
          duration: '01:00', // Default 1 ora
          extendedProps: {
            type: 'project',
            projectId,
            projectName,
            projectColor
          }
        };
      }
    });

    return () => {
      draggable.destroy();
    };
  }, []);

  // Collapsed state
  if (isCollapsed && !isMobile) {
    return (
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="h-12 flex items-center justify-center gap-1 px-1">
          <Link to="/dashboard" className="flex items-center justify-center" data-logo>
            <img src="/icona.png" alt="Logo" className="h-7 w-7 object-contain rounded-lg" />
          </Link>
          {onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              className="flex items-center justify-center w-5 h-5 rounded hover:bg-black/10 transition-colors"
              title="Espandi"
            >
              <ChevronLeft className="h-3.5 w-3.5 rotate-180" />
            </button>
          )}
        </div>

        {/* Collapsed icons */}
        <div className="flex-1 overflow-y-auto px-2 py-4 space-y-2">
          {onBack && (
            <button
              onClick={onBack}
              className="w-full flex items-center justify-center py-1.5 rounded hover:bg-black/5 dark:hover:bg-white/5 transition-colors mb-2"
              title="Menu Principale"
            >
              <LayoutGrid className="h-4 w-4" style={{ color: 'var(--theme-menu-text)' }} />
            </button>
          )}

          <div className="text-[9px] font-semibold uppercase tracking-wide text-center text-muted-foreground/70 mb-2">
            PLAN
          </div>

          {/* Overdue indicator */}
          {overdueTasks.length > 0 && (
            <div className="flex items-center justify-center py-1.5" title={`${overdueTasks.length} in ritardo`}>
              <div className="relative">
                <AlertTriangle className="h-4 w-4 text-red-500" />
                <span className="absolute -top-1 -right-1 text-[8px] font-bold text-red-500">
                  {overdueTasks.length}
                </span>
              </div>
            </div>
          )}

          {/* Da Lavorare indicator */}
          {totalTasksToWork > 0 && (
            <div className="flex items-center justify-center py-1.5" title={`${totalTasksToWork} task da lavorare`}>
              <div className="relative">
                <ListTodo className="h-4 w-4 text-violet-500" />
                <span className="absolute -top-1 -right-1 text-[8px] font-bold text-violet-500">
                  {totalTasksToWork}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Expanded state
  return (
    <div className="h-full flex flex-col" ref={containerRef}>
      {/* Header - hide when sidebar is expanded */}
      {!isMobile && !isSidebarExpanded && (
        <div className="h-12 flex items-center justify-between px-3 flex-shrink-0">
          <Link to="/dashboard" className="flex items-center" style={{ paddingLeft: '6px' }} data-logo>
            <img
              src={isLight ? "/logo_colorato.png" : "/logo_bianco.png"}
              alt="Logo"
              className="h-6 w-auto object-contain"
            />
          </Link>
          {onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              className="flex items-center justify-center w-7 h-7 rounded-md hover:bg-black/10 transition-colors"
              title="Collassa"
            >
              <ChevronLeft className="h-4 w-4" style={{ color: 'var(--theme-menu-text)' }} />
            </button>
          )}
        </div>
      )}

      {/* Mobile header */}
      {isMobile && (
        <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-black/5 dark:border-white/5">
          <Link to="/dashboard" onClick={onClose}>
            <img
              src={isLight ? "/logo_colorato.png" : "/logo_bianco.png"}
              alt="Logo"
              className="h-8 w-auto object-contain"
            />
          </Link>
          {onClose && (
            <button onClick={onClose} className="p-1.5 rounded-full hover:bg-accent transition-colors">
              <X className="h-5 w-5" style={{ color: 'var(--theme-menu-text)' }} />
            </button>
          )}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 flex flex-col min-h-0 p-3 overflow-hidden">
        {/* Expanded header bar */}
        {isSidebarExpanded && (
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-black/10 dark:border-white/10 flex-shrink-0">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <span className="text-sm font-medium text-black/80 dark:text-white/80">
                Dettaglio Task
              </span>
              <span className="text-xs text-black/50 dark:text-white/50">
                ({overdueTasks.length} in ritardo, {totalTasksToWork} da lavorare)
              </span>
            </div>
            <button
              onClick={handleToggleExpanded}
              className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-black/60 dark:text-white/60 hover:bg-black/5 dark:hover:bg-white/5 rounded-md transition-colors"
            >
              <Minimize2 className="h-3.5 w-3.5" />
              <span>Riduci</span>
            </button>
          </div>
        )}

        {/* Back to Menu Button - hide when expanded */}
        {onBack && !isSidebarExpanded && (
          <button
            onClick={onBack}
            className="w-full flex items-center gap-3 py-2 px-3 mb-3 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors text-left flex-shrink-0"
          >
            <LayoutGrid className="h-4 w-4 flex-shrink-0" style={{ color: 'var(--theme-menu-text)' }} />
            <span className="text-sm font-medium" style={{ color: 'var(--theme-menu-text)' }}>
              Menu Principale
            </span>
          </button>
        )}

        {/* Mini Calendar - hide when expanded */}
        {onDateSelect && !isSidebarExpanded && (
          <div className="mb-4 flex-shrink-0">
            <MiniCalendar currentDate={currentDate} onDateSelect={onDateSelect} />
          </div>
        )}

        {/* Search — filtra task/progetti nelle sezioni */}
        <div className="mb-2 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9b9a97] dark:text-gray-500 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cerca task o progetto..."
              className="w-full pl-8 pr-7 py-1.5 text-xs rounded-md bg-white dark:bg-gray-900 text-[#37352f] dark:text-gray-100 placeholder:text-[#9b9a97] dark:placeholder:text-gray-500 border border-black/10 dark:border-white/10 focus:outline-none focus:border-[#2383E2] focus:ring-1 focus:ring-[rgba(35,131,226,0.3)] transition-colors"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
                title="Pulisci"
              >
                <X className="h-3 w-3 text-[#9b9a97] dark:text-gray-500" />
              </button>
            )}
          </div>
        </div>

        {/* Scrollable sections */}
        <div className="flex-1 overflow-y-auto min-h-0 space-y-1">
          {/* TEAM — filtro utenti (solo chi è assegnato ad almeno un task) */}
          {!isSidebarExpanded && (
            <CollapsibleSection
              title="Team"
              count={assignableUsers.length}
              icon={Users}
              iconColor="#2383E2"
              defaultExpanded={true}
            >
              {assignableUsers.length === 0 ? (
                <p className="text-[11px] text-black/40 dark:text-white/40 px-2 py-1">
                  Nessun utente disponibile
                </p>
              ) : (
                <div className="px-1">
                  {/* Fallback: nessun assegnatario → mostro tutto il team */}
                  {noAssignees && (
                    <p className="text-[10px] text-[#787774] dark:text-gray-400 px-1.5 pb-1 leading-snug">
                      Nessun assegnatario: mostro tutto il team
                    </p>
                  )}

                  {/* Tutti / Nessuno */}
                  <div className="flex items-center justify-end gap-1 mb-1">
                    <button
                      onClick={handleSelectAllAssignable}
                      className="text-[10px] font-medium text-[#2383E2] hover:bg-[rgba(35,131,226,0.08)] px-1 py-0.5 rounded transition-colors"
                      title="Seleziona tutti"
                    >
                      Tutti
                    </button>
                    <button
                      onClick={handleDeselectAllUsers}
                      className="text-[10px] font-medium text-[#787774] dark:text-gray-400 hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/[0.06] px-1 py-0.5 rounded transition-colors"
                      title="Deseleziona tutti"
                    >
                      Nessuno
                    </button>
                  </div>

                  <div className="space-y-0.5">
                    {assignableUsers.map(user => {
                      const color = userColorMap.get(user.id) || '#6B7280';
                      const isEnabled = selectedUserIds.includes(user.id);
                      const workload = calculateUserWorkload(user.id, workSlots, weekStart, weekEnd);
                      // Capacità settimanale: carico vs target 40h
                      const capacityColor = getCapacityColor(workload.totalHours, 40);
                      const capacityPct = Math.min(100, (workload.totalHours / 40) * 100);
                      const overloaded = workload.totalHours > 40;

                      return (
                        <button
                          key={user.id}
                          onClick={() => onUserToggle?.(user.id)}
                          className={`w-full flex items-center gap-2 px-1.5 py-1 rounded-md hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.04] transition-colors text-left ${isEnabled ? '' : 'opacity-50'}`}
                        >
                          {/* Checkbox colorato */}
                          <span className="w-3.5 h-3.5 rounded-[3px] flex items-center justify-center flex-shrink-0">
                            {isEnabled ? (
                              <span
                                style={{ backgroundColor: color }}
                                className="w-full h-full rounded-[3px] flex items-center justify-center"
                              >
                                <CheckSquare className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                              </span>
                            ) : (
                              <span className="w-full h-full rounded-[3px] border border-black/20 dark:border-white/25" />
                            )}
                          </span>
                          {/* Pallino colore */}
                          <span
                            className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: color }}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1 min-w-0">
                              <span className="text-xs font-medium text-[#37352f] dark:text-gray-100 truncate leading-tight">
                                {user.full_name || user.email.split('@')[0]}
                              </span>
                              {overloaded && (
                                <span
                                  className="text-[9px] font-medium px-1 py-px rounded-sm flex-shrink-0"
                                  style={{ backgroundColor: '#b91c1c1a', color: '#b91c1c' }}
                                  title="Sovraccarico: oltre 40h settimanali"
                                >
                                  ⚠ sovraccarico
                                </span>
                              )}
                            </div>
                            {/* Barra capacità Xh / 40h */}
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <div className="flex-1 h-1 bg-black/10 dark:bg-white/10 rounded-full overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all"
                                  style={{ width: `${capacityPct}%`, backgroundColor: capacityColor }}
                                />
                              </div>
                              <span
                                className="text-[10px] font-medium leading-none flex-shrink-0"
                                style={{ color: capacityColor }}
                              >
                                {formatHours(workload.totalHours)} / 40h
                              </span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </CollapsibleSection>
          )}

          {/* Overdue Tasks */}
          <CollapsibleSection
            key={`overdue-${normalizedQuery ? 'search' : 'all'}`}
            title="In Ritardo"
            count={overdueTasks.length}
            icon={AlertTriangle}
            iconColor="#dc2626"
            defaultExpanded={!!normalizedQuery}
            onExpand={handleToggleExpanded}
            isSidebarExpanded={isSidebarExpanded}
          >
            {overdueTasks.length === 0 ? (
              <p className="text-[11px] text-black/40 dark:text-white/40 px-2 py-1">
                Nessuna task in ritardo
              </p>
            ) : (
              Array.from(overdueByProject.entries()).map(([projectId, projectTasks]) => (
                <ProjectGroup
                  key={`${projectId}-${normalizedQuery ? 'search' : 'all'}`}
                  projectId={projectId}
                  tasks={projectTasks}
                  project={projectMap.get(projectId)}
                  showOverdueBadge={true}
                  defaultExpanded={isSidebarExpanded || !!normalizedQuery}
                  isSidebarExpanded={isSidebarExpanded}
                  onOpenProject={handleOpenProject}
                  users={users}
                  onCompleteTask={handleCompleteTask}
                  onTaskClick={handleOpenTask}
                  scheduledTaskIds={scheduledTaskIds}
                  scheduledHoursByTask={scheduledHoursByTask}
                />
              ))
            )}
          </CollapsibleSection>

          {/* Da Lavorare - Tutte le task non completate */}
          {allTasksByProject.size > 0 && (
            <CollapsibleSection
              key={`towork-${normalizedQuery ? 'search' : 'all'}`}
              title="Da Lavorare"
              count={totalTasksToWork}
              icon={ListTodo}
              iconColor="#8b5cf6"
              defaultExpanded={!!normalizedQuery}
              onExpand={handleToggleExpanded}
              isSidebarExpanded={isSidebarExpanded}
            >
              {Array.from(allTasksByProject.entries()).map(([projectId, projectTasks]) => (
                <ProjectGroup
                  key={`${projectId}-${normalizedQuery ? 'search' : 'all'}`}
                  projectId={projectId}
                  tasks={projectTasks}
                  project={projectMap.get(projectId)}
                  showOverdueBadge={false}
                  defaultExpanded={isSidebarExpanded || !!normalizedQuery}
                  isSidebarExpanded={isSidebarExpanded}
                  onOpenProject={handleOpenProject}
                  users={users}
                  onCompleteTask={handleCompleteTask}
                  onTaskClick={handleOpenTask}
                  scheduledTaskIds={scheduledTaskIds}
                  scheduledHoursByTask={scheduledHoursByTask}
                />
              ))}
            </CollapsibleSection>
          )}

          {/* Workload */}
          <CollapsibleSection
            title="Workload"
            count={users.length}
            icon={Users}
            iconColor="#3b82f6"
            defaultExpanded={false}
          >
            {userWorkloads.length === 0 ? (
              <p className="text-[11px] text-black/40 dark:text-white/40 px-2 py-1">
                Nessun utente selezionato
              </p>
            ) : (
              <div className="space-y-1">
                {userWorkloads.map(({ user, workload }) => (
                  <WorkloadItem
                    key={user.id}
                    user={user}
                    hours={workload.totalHours}
                  />
                ))}
              </div>
            )}
          </CollapsibleSection>
        </div>
      </div>

      {/* Project Details Modal */}
      <ProjectDetailsModal
        projectId={selectedProjectId}
        isOpen={!!selectedProjectId}
        onClose={() => setSelectedProjectId(null)}
      />

      {/* Task Drawer - rendered via Portal to escape sidebar DOM hierarchy */}
      {createPortal(
        <TaskDrawer
          task={selectedTask}
          isOpen={showTaskDrawer}
          onClose={() => {
            setSelectedTask(null);
            setShowTaskDrawer(false);
          }}
          onUpdate={handleUpdateTask}
          onDelete={handleDeleteTask}
          onAddComment={handleAddComment}
        />,
        document.body
      )}
    </div>
  );
}
